import React from "react";
import { Link } from "react-router-dom";
import "./styles/Connectivity.css";

const transportOptions = [
  {
    title: "Broadband",
    text: "Flexible internet connectivity for businesses that need practical and scalable access across locations.",
  },
  {
    title: "Dedicated Internet",
    text: "Higher performance internet connectivity for environments that need stronger reliability and consistency.",
  },
  {
    title: "MPLS",
    text: "A proven WAN transport for connecting locations with more controlled traffic handling across the network.",
  },
  {
    title: "Ethernet",
    text: "Business grade connectivity for stable, high speed network performance across enterprise environments.",
  },
  {
    title: "Private Line",
    text: "Direct connectivity options for organizations that need more isolated and dedicated communication paths.",
  },
  {
    title: "Fiber Connectivity",
    text: "High capacity infrastructure support for fast and scalable connectivity across business operations.",
  },
];

const topologyPoints = [
  {
    title: "Centralized SDN control",
    text: "Software defined networking helps optimize traffic flow and simplify how different connectivity services are managed across the environment.",
  },
  {
    title: "Integrated transport mix",
    text: "Combine broadband, MPLS, Ethernet, dedicated internet, and private line services into a more unified network model.",
  },
  {
    title: "Flexible access options",
    text: "Support business needs with gigabit services, 4G and 5G LTE, fixed wireless, private wireless, and fiber based access paths.",
  },
  {
    title: "Physical infrastructure support",
    text: "Structured wiring and network infrastructure services help ensure the physical foundation is aligned with connectivity goals.",
  },
];

const accessServices = [
  {
    label: "Gigabit services",
    text: "Support higher throughput business use cases with strong bandwidth capacity for modern applications.",
  },
  {
    label: "4G / 5G LTE",
    text: "Add mobility, backup connectivity, or flexible deployment options where wired access is limited or needs redundancy.",
  },
  {
    label: "Fixed wireless",
    text: "Extend connectivity options where rapid deployment or alternative access methods are important.",
  },
  {
    label: "Private wireless",
    text: "Enable more controlled wireless environments for business operations that require dedicated coverage or performance.",
  },
];

const outcomeCards = [
  {
    title: "Better network performance",
    text: "Optimize connectivity across multiple services and access types with a more coordinated networking approach.",
  },
  {
    title: "Greater deployment flexibility",
    text: "Mix wired and wireless options to support branch offices, distributed teams, and changing operational needs.",
  },
  {
    title: "Infrastructure aligned to business needs",
    text: "Build around the right blend of transport, capacity, and physical network support rather than one fixed model.",
  },
];

const fitCards = [
  {
    title: "Multi site businesses",
    text: "For organizations that need to connect offices, branches, users, and services across locations more efficiently.",
  },
  {
    title: "Bandwidth heavy operations",
    text: "For businesses that rely on stable, scalable connectivity for cloud apps, communications, and day to day workflows.",
  },
  {
    title: "Evolving infrastructure needs",
    text: "For teams that need the flexibility to combine fiber, wireless, broadband, and private access services over time.",
  },
];

export default function Connectivity() {
  return (
    <div className="connectivityPage">
      <section className="connectivityHero">
        <div className="connectivityContainer connectivityHero__container">
          <div className="connectivityHero__content">
            <p className="connectivityEyebrow">Connectivity</p>

            <h1 className="connectivityHero__title">
              Smarter network design for connected business environments.
            </h1>

            <p className="connectivityHero__text">
              Our software defined networking solutions optimize network
              performance by bringing together broadband, dedicated internet,
              MPLS, Ethernet, private line services, wireless access, fiber
              connectivity, and structured wiring into one comprehensive
              connectivity approach tailored to your business needs.
            </p>

            <div className="connectivityHero__actions">
              <Link
                to="/contact"
                className="connectivityBtn connectivityBtn--primary"
              >
                Talk to Our Team
              </Link>
              <Link
                to="/services"
                className="connectivityBtn connectivityBtn--secondary"
              >
                Explore Services
              </Link>
            </div>
          </div>

          <div className="connectivityHero__visual">
            <div className="connectivityBlueprint">
              <div className="connectivityBlueprint__header">
                <span className="connectivityChip">SDN powered</span>
                <span className="connectivityChip connectivityChip--soft">
                  Multi transport
                </span>
              </div>

              <div className="connectivityBlueprint__map">
                <div className="connectivityNode connectivityNode--core">
                  SDN Core
                </div>

                <div className="connectivityNode connectivityNode--one">
                  Broadband
                </div>
                <div className="connectivityNode connectivityNode--two">
                  MPLS
                </div>
                <div className="connectivityNode connectivityNode--three">
                  Ethernet
                </div>
                <div className="connectivityNode connectivityNode--four">
                  Private Line
                </div>
                <div className="connectivityNode connectivityNode--five">
                  LTE / 5G
                </div>

                <span className="connectivityLine connectivityLine--one" />
                <span className="connectivityLine connectivityLine--two" />
                <span className="connectivityLine connectivityLine--three" />
                <span className="connectivityLine connectivityLine--four" />
                <span className="connectivityLine connectivityLine--five" />
              </div>

              <div className="connectivityBlueprint__footer">
                <div className="connectivityMetric">
                  <strong>Integrated transports</strong>
                  <span>Wired and wireless connectivity working together</span>
                </div>
                <div className="connectivityMetric">
                  <strong>Business ready delivery</strong>
                  <span>Tailored to performance, scale, and site needs</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="connectivityMatrixSection">
        <div className="connectivityContainer">
          <div className="connectivitySectionHead connectivitySectionHead--center">
            <p className="connectivityEyebrow">Transport options</p>
            <h2 className="connectivityTitle">
              Connectivity services designed to work as one network strategy
            </h2>
            <p className="connectivityText">
              Modern business connectivity often requires more than one service
              type. SDN makes it easier to coordinate multiple transport options
              into a more optimized and flexible network model.
            </p>
          </div>

          <div className="connectivityMatrix">
            {transportOptions.map((item) => (
              <article key={item.title} className="connectivityMatrixCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="connectivityTopologySection">
        <div className="connectivityContainer connectivityTopologyWrap">
          <div className="connectivityTopologyIntro">
            <p className="connectivityEyebrow">Network approach</p>
            <h2 className="connectivityTitle">
              A connectivity architecture shaped around performance and flexibility
            </h2>
            <p className="connectivityText">
              Software defined networking allows organizations to bring
              different network transports and access models under a more
              centralized and adaptable operational approach.
            </p>
          </div>

          <div className="connectivityTopologyGrid">
            {topologyPoints.map((item, index) => (
              <article key={item.title} className="connectivityTopologyCard">
                <span className="connectivityTopologyCard__index">
                  0{index + 1}
                </span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="connectivityAccessSection">
        <div className="connectivityContainer">
          <div className="connectivityAccessBand">
            <div className="connectivityAccessBand__intro">
              <p className="connectivityEyebrow">Access services</p>
              <h2 className="connectivityTitle">
                Connectivity choices for different locations, speeds, and deployment needs
              </h2>
              <p className="connectivityText">
                From gigabit services and LTE to fixed wireless, private
                wireless, and fiber, the right connectivity plan depends on how
                your business sites, users, and applications need to connect.
              </p>
            </div>

            <div className="connectivityAccessGrid">
              {accessServices.map((item) => (
                <article key={item.label} className="connectivityAccessCard">
                  <strong>{item.label}</strong>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="connectivityInfraSection">
        <div className="connectivityContainer">
          <div className="connectivityInfra">
            <div className="connectivityInfra__left">
              <p className="connectivityEyebrow">Infrastructure services</p>
              <h2 className="connectivityTitle">
                Connectivity is stronger when the physical foundation is planned well
              </h2>
            </div>

            <div className="connectivityInfra__right">
              <p className="connectivityText">
                Structured wiring and supporting infrastructure services play an
                important role in overall connectivity performance. Strong
                business networks rely not only on service selection, but also
                on how the physical environment is designed and connected.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="connectivityOutcomesSection">
        <div className="connectivityContainer">
          <div className="connectivitySectionHead">
            <p className="connectivityEyebrow">Business outcomes</p>
            <h2 className="connectivityTitle">
              What this connectivity approach helps improve
            </h2>
            <p className="connectivityText">
              The goal is not simply to add more connections. It is to create a
              more usable, scalable, and better aligned network environment for
              business operations.
            </p>
          </div>

          <div className="connectivityOutcomeGrid">
            {outcomeCards.map((item) => (
              <article key={item.title} className="connectivityOutcomeCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="connectivityFitSection">
        <div className="connectivityContainer">
          <div className="connectivitySectionHead connectivitySectionHead--center">
            <p className="connectivityEyebrow">Best fit</p>
            <h2 className="connectivityTitle">
              Where this service creates the most value
            </h2>
            <p className="connectivityText">
              Connectivity solutions are most effective when they match how the
              business actually operates across sites, users, applications, and
              growth plans.
            </p>
          </div>

          <div className="connectivityFitGrid">
            {fitCards.map((item) => (
              <article key={item.title} className="connectivityFitCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="connectivityCta">
        <div className="connectivityContainer connectivityCta__container">
          <div>
            <p className="connectivityEyebrow">Next step</p>
            <h2 className="connectivityCta__title">
              Build a connectivity strategy that fits your business environment
            </h2>
            <p className="connectivityCta__text">
              If your organization needs a smarter mix of broadband, internet,
              MPLS, Ethernet, private access, wireless, fiber, and structured
              wiring support, ComNetworks.net can help shape the right solution.
            </p>
          </div>

          <div className="connectivityCta__actions">
            <Link
              to="/contact"
              className="connectivityBtn connectivityBtn--primary"
            >
              Contact Us
            </Link>
            <Link
              to="/services"
              className="connectivityBtn connectivityBtn--outline"
            >
              View More Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
